'use client';

import React, { useState } from 'react';
import { AddIcon, SubtractIcon } from '@/components/ui/Icons';

interface QuantitySelectorProps {
  initialQuantity?: number;
  onQuantityChange?: (quantity: number) => void;
  className?: string;
}

const QuantitySelector: React.FC<QuantitySelectorProps> = ({
  initialQuantity = 1,
  onQuantityChange,
  className = '',
}) => {
  const [quantity, setQuantity] = useState(initialQuantity);

  const handleDecrease = () => {
    const newQuantity = Math.max(0, quantity - 1);
    setQuantity(newQuantity);
    onQuantityChange?.(newQuantity);
  };

  const handleIncrease = () => {
    const newQuantity = quantity + 1;
    setQuantity(newQuantity);
    onQuantityChange?.(newQuantity);
  };

  return (
    <div
      className={`flex items-center rounded-md px-[14.5px] border text-gray-700 border-grey-mid-light h-8 ${className}`}
      data-testid="quantity-selector"
      role="group"
      aria-label="Quantity selector"
    >
      <button
        className="flex items-center justify-center hover:text-gray-900 transition-colors"
        aria-label="Decrease quantity"
        type="button"
        onClick={handleDecrease}
        data-testid="quantity-decrease-button"
      >
        <SubtractIcon height="10" width="10" color="#424140" />
      </button>
      <span
        className="text-sm font-bold px-4 leading-[20px]"
        data-testid="quantity-display"
        aria-live="polite"
        aria-label={`Current quantity: ${quantity}`}
      >
        {quantity}
      </span>
      <button
        className="flex items-center justify-center hover:text-gray-900 transition-colors"
        aria-label="Increase quantity"
        type="button"
        onClick={handleIncrease}
        data-testid="quantity-increase-button"
      >
        <AddIcon height="10" width="10" color="#424140" />
      </button>
    </div>
  );
};

export default QuantitySelector;
